import { registerDecorators as _registerDecorators, registerComponent as _registerComponent, LightningElement } from "lwc";
import _tmpl from "./pricingSummaryV2.html";
import { schemas } from '@oneappexchange/oaf-schema';
import { getPlanPrice, getPlanUnits, hasDiscountNonProfits, isFreeListing, isFreemiumListing, isFreeToInstallListing, isLowestStartingPrice, isPaidListing } from '~/modules/helpers/pricingHelper';
import { Routes } from '~/modules/routes';
import { i18nStore, WireI18n } from '~/modules/stores/i18nStore';
import { buildFrequencyLabels, buildUnitLabels, capitalizeWords } from '~/modules/utilities/pricingUtilities';
import { upsertQueryParams } from '~/modules/utilities/router';

/**
 * v2 pricing summary — the text block inside the Price & CTA card of
 * `pages/listingDetailV2`'s sticky side panel.
 *
 * This began as a STANDALONE WHOLESALE COPY of the former v1
 * `listing/pricingSummary` (not a subclass) — same rationale as
 * `listing/overviewV2` (see its docblock): while v1 (`pages/listingDetail`)
 * still rendered `listing-pricing-summary`, v2 forked rather than editing or
 * inheriting. v1 `listing/pricingSummary` has since been deleted at 100% rollout
 * (W-24557579). The pricing/frequency derivation stays as v1's and the
 * divergence is mostly presentational — template + CSS (Figma 7764:135352:
 * centered headline, stacked price/units, "Pricing Details" as a bold-blue link)
 * — with one intentional TS difference: `summaryHeadlinePaidPlanDetails`
 * title-cases the displayed unit/cadence to match the v2 listing-detail
 * presentation.
 */
class PricingSummaryV2 extends LightningElement {
  constructor(...args) {
    super(...args);
    this.pricing = void 0;
  }
  i18nCallback(i18n) {
    this.staticLabels = i18n.listingDetails.pricing;
    this.frequencyLabels = buildFrequencyLabels(this.staticLabels);
    this.unitLabels = buildUnitLabels(this.staticLabels);
  }

  /**
   * Strictly-free listings (`price_model_type === 'free'`) have no Pricing tab
   * (see listingDetailV2's `showPricingTab`), so the "Pricing Details" jump link
   * — which deep-links to that tab — is hidden for them. Freemium / free-to-install
   * / paid listings keep the link.
   */
  get showPricingDetailsLink() {
    return !isFreeListing(this.pricing);
  }

  // v2 routes "Pricing Details" to the dedicated Pricing tab (`p`), not
  // Overview (`e`) as in v1 — in v2 the full pricing breakdown lives in its
  // own tab. The `scrollTo=pricing-details` anchor still lands on the pricing
  // details header (`listing-pricing` reads it from the router). Rendered as a
  // slotted `<a>` in a `wds-link` (see the template) rather than an
  // i18n-message so the anchor's underline can be styled off directly.
  get pricingDetailsHref() {
    return `${Routes.LISTING}?${upsertQueryParams(['tab', 'p'], ['scrollTo', 'pricing-details'])}`;
  }
  get pricingDetailsLabel() {
    return this.staticLabels?.pricingDetails ?? '';
  }

  // PRICING DATA FROM OAF
  get defaultPricingPlan() {
    if (this.pricing?.model?.plans) {
      const plans = [...this.pricing.model.plans];
      plans.sort((a, b) => a.index - b.index);
      return plans[0];
    }
    return schemas['pricing_plan/pricing/PricePlan'].new();
  }
  get defaultPlanCurrencyCode() {
    return this.defaultPricingPlan.currency_code;
  }
  get defaultPlanPrice() {
    return this.defaultPricingPlan.price;
  }
  get defaultPlanFrequency() {
    // @ts-expect-error * Automated Type Error Fix
    return this.frequencyLabels[this.defaultPricingPlan.frequency] ?? '';
  }
  get summaryHeadline() {
    if (isPaidListing(this.pricing)) {
      if (this.pricing?.model?.plans?.length === 0) {
        return this.staticLabels.paidListing;
      }
      const price = getPlanPrice(this.defaultPlanCurrencyCode, this.defaultPlanPrice);
      return isLowestStartingPrice(this.defaultPricingPlan) ? i18nStore.getState().i18n.listingDetails.pricing.startingAt({
        PRICE: price
      }) : price;
    }
    // Free-to-install listings always show the "Paid Add-On Required" label,
    // even when they carry add-on plans (per UX — the sidebar never surfaces the
    // add-on price here; the plan breakdown lives in the Pricing tab).
    if (isFreeToInstallListing(this.pricing)) {
      return this.staticLabels.freeToInstallSubSectionHeader;
    }
    if (isFreemiumListing(this.pricing)) {
      return this.staticLabels.freemiumSubSectionHeader;
    }
    return this.staticLabels.freeSummaryHeadline;
  }

  /**
   * Localizes the standard `user`/`company` units; falls back to the raw value
   * for `custom` units, which carry publisher free-text (`custom_unit`) we don't
   * translate. getPlanUnits already resolves `custom` to that free-text string.
   */
  get defaultPlanUnits() {
    const units = getPlanUnits(this.defaultPricingPlan);
    if (units === 'user' || units === 'company') {
      return this.unitLabels?.[units] ?? units;
    }
    return units;
  }
  get summaryHeadlinePaidPlanDetails() {
    // @ts-expect-error * Automated Type Error Fix
    if (isPaidListing(this.pricing) && this.pricing.model?.plans?.length > 0) {
      return capitalizeWords(`${this.defaultPlanUnits}/${this.defaultPlanFrequency}`);
    }
    return '';
  }
  get inlineHeadline() {
    // Tablet/mobile collapse the price and its per-unit/frequency cadence onto
    // a single line (e.g. "Starting at $24.99/User/Month"); desktop keeps them
    // on separate lines. Non-paid models (Free/Freemium/Free to Install) carry
    // no cadence, so they fall back to the plain headline.
    if (this.summaryHeadlinePaidPlanDetails) {
      return `${this.summaryHeadline}/${this.summaryHeadlinePaidPlanDetails}`;
    }
    return this.summaryHeadline;
  }

  /**
   * First line of the sidebar description — the base, per-model text (no trailing
   * period; this is a summary label, not a sentence). The nonprofit-discount note
   * renders on its own second line via `summaryNonProfitDescription`, so this stays
   * the plain base copy. Paid listings carry no base description.
   */
  get summaryDescription() {
    if (isPaidListing(this.pricing)) {
      return '';
    }
    if (isFreeToInstallListing(this.pricing)) {
      return this.staticLabels.freeToInstallSummaryDescription;
    }
    if (isFreemiumListing(this.pricing)) {
      return this.staticLabels.freemiumSummaryDescription;
    }
    return this.staticLabels.freeSummaryDescription;
  }

  /**
   * Second line of the sidebar description — "Discounts for nonprofits" when the
   * listing offers nonprofit discounts, otherwise empty. The note is the same
   * across every pricing model, so all models share `summaryDescriptionNonProfits`.
   * Rendered beneath `summaryDescription` so the two read as stacked lines (e.g.
   * "Start for free" / "Discounts for nonprofits"). The pricing tab combines these
   * into one punctuated sentence instead (see pricingV2's `pricingDescription`).
   */
  get summaryNonProfitDescription() {
    return hasDiscountNonProfits(this.pricing) ? this.staticLabels.summaryDescriptionNonProfits : '';
  }
  handleI18nLinkClick(event) {
    // The Pricing Details link is a slotted `<a>` (not an i18n-message), so `wds-link`'s
    // built-in click interception doesn't reliably fire for it — without this the browser
    // follows the anchor's href and does a full-page reload. Prevent the default navigation
    // so the page-level `linkclick` handler does a SPA navigation to the Pricing tab instead.
    event.preventDefault();
    const analyticsEvent = new CustomEvent('linkclick');
    this.dispatchEvent(analyticsEvent);
  }
  /*LWC compiler v7.1.3*/
}
_registerDecorators(PricingSummaryV2, {
  publicProps: {
    pricing: {
      config: 0
    }
  },
  wire: {
    i18nCallback: {
      adapter: WireI18n,
      method: 1,
      config: function ($cmp) {
        return {};
      }
    }
  }
});
const __lwc_component_class_internal = _registerComponent(PricingSummaryV2, {
  tmpl: _tmpl,
  sel: "",
  apiVersion: 62
});
export default __lwc_component_class_internal;